import React, { useCallback, useEffect, useState } from 'react'
import { CBadge, CCollapse, CSmartTable } from '@coreui/react-pro'
import moment from 'moment'

import ToastComponent from 'src/components/common/TaostComponent.js'
import TableSearchToolbar from 'src/components/common/TableSearchToolbar'
import RowActionsMenu from 'src/components/common/RowActionsMenu'
import UserService from 'src/service/UserService'

const SEARCH_FIELDS = [
  { key: 'full_name', label: 'Member' },
  { key: 'id', label: 'Member ID' },
]

const columns = [
  { label: 'Member ID', key: 'id', filter: false, sorter: true, _style: { width: '10%' } },
  { label: 'Member', key: 'full_name', filter: false, sorter: true },
  { label: 'Team Picked', key: 'team_exists', filter: false, sorter: false },
  { label: 'Suspended', key: 'is_suspend', filter: false, sorter: true },
  { key: 'action', label: 'Actions', filter: false, sorter: false, _style: { width: '1%' } },
]

const ManageMember = ({ onTotalChange }) => {
  const [loading, setLoading] = useState(false)
  const [activePage, setActivePage] = useState(1)
  const [columnFilter, setColumnFilter] = useState({})
  const [searchField, setSearchField] = useState('full_name')
  const [columnSorter, setColumnSorter] = useState(null)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [users, setUsers] = useState()

  // Only one member's detail row is open at a time; userDetails holds that member's profile.
  const [details, setDetails] = useState([])
  const [userDetails, setUserDetails] = useState({})

  const clearSearchKeys = (filter) => {
    const next = { ...filter }
    SEARCH_FIELDS.forEach((f) => delete next[f.key])
    return next
  }

  const applySearch = (value) => {
    setActivePage(1)
    const next = clearSearchKeys(columnFilter)
    if (value) next[searchField] = value
    setColumnFilter(next)
  }

  const handleFieldChange = (key) => {
    setSearchField(key)
    setColumnFilter((prev) => clearSearchKeys(prev))
  }

  const fetchData = useCallback(() => {
    const offset = itemsPerPage * activePage - itemsPerPage
    const params = new URLSearchParams()
    Object.keys(columnFilter).forEach((key) => {
      if (columnFilter[key]) params.append(key, columnFilter[key])
    })
    if (columnSorter && columnSorter.column !== undefined) {
      params.append('sort', `${columnSorter.column}%${columnSorter.state}`)
    }

    setLoading(true)
    UserService.getUser(offset, itemsPerPage, activePage, params)
      .then((result) => {
        setUsers(result.data)
        onTotalChange?.(result.data?.total ?? 0)
        setLoading(false)
      })
      .catch(() => {
        setLoading(false)
      })
  }, [activePage, columnFilter, columnSorter, itemsPerPage, onTotalChange])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const toggleDetails = (index, userId) => {
    if (details.includes(index)) {
      setDetails([])
      return
    }
    setDetails([index])
    UserService.getUserDetail(userId).then((res) => {
      if (res.status === 200) {
        setUserDetails(res.data)
      }
    })
  }

  // The suspend endpoint returns its own listing, but without the team_exists / is_suspend
  // flags this table renders, so re-read the listing instead of trusting that payload.
  const suspendMember = (userId, status) => {
    UserService.suspendMember(userId, status).then((res) => {
      ToastComponent(res.message, 'success')
      fetchData()
    })
  }

  const activeSearchLabel = SEARCH_FIELDS.find((f) => f.key === searchField)?.label

  return (
    <>
      <div className="um-panel-heading">
        <div>
          <h2 className="um-panel-title">Manage Members</h2>
          <p className="um-panel-note">Check team status and control access for each member.</p>
        </div>
      </div>

      <TableSearchToolbar
        fields={SEARCH_FIELDS}
        field={searchField}
        onFieldChange={handleFieldChange}
        value={columnFilter?.[searchField] || ''}
        onSearch={applySearch}
        placeholder={`Search by ${activeSearchLabel}...`}
      />

      <div className="ge-datatable ge-datatable--no-select ge-datatable--static">
        <CSmartTable
          columns={columns}
          columnSorter={{ external: true }}
          noItemsLabel="No members found"
          scopedColumns={{
            id: (item) => <td className="um-muted">{item.id}</td>,
            full_name: (item) => <td className="fw-semibold">{item.full_name}</td>,
            team_exists: (item) => (
              <td>
                <CBadge
                  color={item?.team_exists ? 'success' : 'secondary'}
                  shape="rounded-pill"
                  className="px-2 py-1"
                >
                  {item?.team_exists ? 'Yes' : 'No'}
                </CBadge>
              </td>
            ),
            is_suspend: (item) => (
              <td>
                <CBadge
                  color={item?.is_suspend ? 'danger' : 'success'}
                  shape="rounded-pill"
                  className="px-2 py-1"
                >
                  {item?.is_suspend ? 'Yes' : 'No'}
                </CBadge>
              </td>
            ),
            action: (item) => (
              <td>
                <RowActionsMenu
                  actions={[
                    {
                      label: details.includes(item.id) ? 'Hide Info' : 'Info',
                      onClick: () => toggleDetails(item.id, item.user_id),
                    },
                    item?.is_suspend
                      ? {
                          label: 'Re-Instate Access',
                          onClick: () => suspendMember(item.user_id, 0),
                        }
                      : {
                          label: 'Suspend Access',
                          danger: true,
                          onClick: () => suspendMember(item.user_id, 1),
                        },
                  ]}
                />
              </td>
            ),
            details: (item) => (
              <CCollapse visible={details.includes(item.id)}>
                <div className="um-member-details">
                  <div className="um-member-detail">
                    <span className="um-member-detail-label">Name</span>
                    <span className="um-member-detail-value">{userDetails?.full_name || '-'}</span>
                  </div>
                  <div className="um-member-detail">
                    <span className="um-member-detail-label">Email</span>
                    <span className="um-member-detail-value">{userDetails?.email || '-'}</span>
                  </div>
                  <div className="um-member-detail">
                    <span className="um-member-detail-label">Registered</span>
                    <span className="um-member-detail-value">
                      {userDetails?.created_at
                        ? moment(userDetails.created_at).format('D.MM.YYYY')
                        : '-'}
                    </span>
                  </div>
                  <div className="um-member-detail">
                    <span className="um-member-detail-label">Phone</span>
                    <span className="um-member-detail-value">{userDetails?.phone || '-'}</span>
                  </div>
                  <div className="um-member-detail">
                    <span className="um-member-detail-label">Team Name</span>
                    <span className="um-member-detail-value">
                      {userDetails?.my_team_name || '-'}
                    </span>
                  </div>
                </div>
              </CCollapse>
            ),
          }}
          items={users?.data}
          itemsPerPage={itemsPerPage}
          itemsPerPageSelect
          loading={loading}
          pagination={{ external: true }}
          paginationProps={{
            activePage,
            pages: Math.ceil(users?.total / itemsPerPage) || 1,
          }}
          tableProps={{ hover: true, responsive: true, className: 'align-middle mb-0' }}
          onActivePageChange={(page) => setActivePage(page)}
          onItemsPerPageChange={(perPage) => {
            setActivePage(1)
            setItemsPerPage(perPage)
          }}
          onSorterChange={(sorter) => setColumnSorter(sorter)}
        />
      </div>
    </>
  )
}

export default ManageMember
